import { useEffect, useState } from "react";

/** Version shown when the site runs without a built `release.json`. */
export const FALLBACK_VERSION = "0.2.0-beta.1";

export interface ReleaseInfo {
  version: string;
  sizeBytes: number;
  sha256: string;
  builtAt: string;
}

export interface PublishedRelease extends ReleaseInfo {
  type: string;
  name: string;
  description: string;
  file: string;
  current: boolean;
}

function isReleaseInfo(value: unknown): value is ReleaseInfo {
  if (value === null || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.version === "string" &&
    typeof record.sizeBytes === "number" &&
    typeof record.sha256 === "string" &&
    typeof record.builtAt === "string"
  );
}

/**
 * Reads the manifest written by `scripts/build-site.mjs` next to the installer.
 * When the file is absent (dev server, or a site built before the installer),
 * the hook returns null so the page can say the size and hash are not available
 * instead of inventing them.
 */
export function useReleaseInfo(): ReleaseInfo | null {
  const [release, setRelease] = useState<ReleaseInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/downloads/release.json", { cache: "no-cache" })
      .then((response) => (response.ok ? response.json() : null))
      .then((value: unknown) => {
        if (cancelled || !isReleaseInfo(value)) return;
        setRelease(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return release;
}

export function usePublishedReleases(): PublishedRelease[] {
  const [releases, setReleases] = useState<PublishedRelease[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetch("/downloads/releases.json", { cache: "no-cache" })
      .then((response) => response.ok ? response.json() : [])
      .then((value: unknown) => {
        if (!cancelled && Array.isArray(value)) setReleases(value.filter((entry): entry is PublishedRelease =>
          entry && typeof entry.version === "string" && typeof entry.type === "string" && typeof entry.name === "string" &&
          typeof entry.description === "string" && typeof entry.file === "string" && typeof entry.sizeBytes === "number" &&
          typeof entry.sha256 === "string" && typeof entry.builtAt === "string" && typeof entry.current === "boolean"));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);
  return releases;
}

export function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatReleaseDate(value: string | null): string {
  if (!value) return "date not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "date not recorded";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function shortHash(value: string): string {
  return value.length > 20 ? `${value.slice(0, 16)}…` : value;
}
